angular.module('ClinicApp').factory('IframeService', (ClinicConfig, OCLogUtil) => {
  class IframeNotifier {
    #iframeListeners;

    constructor() {
      this.#iframeListeners = new Map();
    }

    #getIframeListeners = (type) => {
      const existingListeners = this.#iframeListeners.get(type);
      if (existingListeners) {
        return existingListeners;
      }

      const newListeners = new Set();
      this.#iframeListeners.set(type, newListeners);
      return newListeners;
    };

    onIframeMessage = (event) => {
      if (event.origin !== ClinicConfig.CLINIC_APP_V4_URL) {
        return;
      }

      const data = event?.data;
      if (this.#shouldIgnoreMessage(data)) {
        return;
      }

      if (!data || typeof data !== 'object') {
        OCLogUtil.rollbar.error('Invalid iframe message received', { data });
        return;
      }

      const type = data.type;
      if (!type) {
        OCLogUtil.rollbar.error('No iframe message type received', { data });
        return;
      }

      const listeners = this.#getIframeListeners(type);
      // The source window is forwarded so listeners can reply to the exact iframe
      // that sent the message. Messages are only distinguishable by type otherwise.
      const sender = { source: event.source, origin: event.origin };
      listeners.forEach((listener) => listener(data, sender));
    };

    addIframeListener = (type, listener) => {
      const listeners = this.#getIframeListeners(type);
      listeners.add(listener);
    };

    removeIframeListener = (type, listener) => {
      const listeners = this.#getIframeListeners(type);
      listeners.delete(listener);
    };

    #shouldIgnoreMessage = (data) => {
      // Not the best way to handle this but there are no indications on the event that
      // it's a fullstory message. We could parse the data and check for the __fs key but
      // FullStory sends a lot of messages and this is more performant.
      const isFullStoryMessage = typeof data === 'string' && data.startsWith('{"__fs":');
      return isFullStoryMessage;
    };
  }

  class IframeFunctionCallHandler {
    #scope;
    #iframeService;

    constructor($scope, iframeService) {
      this.#scope = $scope;
      this.#iframeService = iframeService;
    }

    get type() {
      return 'function-call';
    }

    onMessage = (data) => {
      const functionName = data.function;
      const func = this.#scope[functionName];
      const functionExists = typeof func === 'function';
      const args = data.args;

      if (!functionExists) {
        const functionsInCurrentScope = Object.keys(this.#scope).filter((key) => typeof this.#scope[key] === 'function');

        const otherScopes = this.#iframeService.allFunctionScopes.map((scope, index) => {
          const functionsInScope = Object.keys(scope).filter((key) => typeof scope[key] === 'function');
          return {
            index,
            functionsInScope,
          };
        });

        const functionExistsInOtherScopes =
          otherScopes.some(({ functionsInScope }) => functionsInScope.includes(functionName));

        if (!functionExistsInOtherScopes) {
          OCLogUtil.rollbar.error('iframe tried to call function that does not exist', {
            functionName,
            args,
            functionsInCurrentScope,
            otherScopes,
          });
        }
        return;
      }

      if (args) {
        return func(args);
      }

      return func();
    };
  }

  class IframeResizeHandler {
    #getElement;

    #isApplicable;

    constructor(getElement, isApplicable) {
      this.#getElement = getElement;
      this.#isApplicable = isApplicable;
    }

    get type() {
      return 'resize';
    }

    onMessage = (data) => {
      if (!this.#isApplicable()) {
        return;
      }

      const { height, width } = data;
      if (typeof height !== 'number' && typeof width !== 'number') {
        OCLogUtil.rollbar.error('Invalid size data received from clinic-v4', { data });
        return;
      }

      const element = this.#getElement();
      if (!element) {
        return;
      }

      if (height) {
        element.style.height = `${height}px`;
      }

      if (width) {
        element.style.width = `${width}px`;
      }
    };
  }

  class IframeService {
    #notifier;
    #allFunctionScopes;

    constructor() {
      this.#notifier = new IframeNotifier();
      this.#allFunctionScopes = [];
      window.addEventListener('message', this.#notifier.onIframeMessage);
    }

    get allFunctionScopes() {
      return this.#allFunctionScopes;
    }

    setupFunctionCallHandler = ($scope) => {
      this.#allFunctionScopes.push($scope);

      const functionCallHandler = new IframeFunctionCallHandler($scope, this);
      this.onMessage(functionCallHandler.type, functionCallHandler.onMessage);

      $scope.$on('$destroy', () => {
        this.#allFunctionScopes = this.#allFunctionScopes.filter((scope) => scope !== $scope);
        this.offMessage(functionCallHandler.type, functionCallHandler.onMessage);
      });
    };

    addResizeHandler = ({
      getElement,
      isApplicable = () => true,
    }) => {
      const handler = new IframeResizeHandler(getElement, isApplicable);
      this.onMessage(handler.type, handler.onMessage);
    };

    sendMessage = ({
      type,
      data,
      iframe,
    }) => {
      const contentWindow = iframe?.contentWindow;
      if (!contentWindow) {
        OCLogUtil.rollbar.error(
          'Iframe content window not found while sending postMessage',
          {
            iframe: {
              id: iframe?.id,
              name: iframe?.name,
              src: iframe?.src,
            },
            type,
            data,
          },
        );
        return;
      }

      iframe.contentWindow.postMessage(
        { type, data },
        '*',
      );
    };

    // Used to reply to an iframe we only know by its message source window,
    // rather than by holding its DOM element.
    sendMessageToWindow = ({
      type,
      data,
      targetWindow,
    }) => {
      if (!targetWindow) {
        OCLogUtil.rollbar.error(
          'Target window not found while sending postMessage',
          { type, data },
        );
        return;
      }

      targetWindow.postMessage(
        { type, data },
        '*',
      );
    };

    onMessage = (type, listener) => {
      this.#notifier.addIframeListener(type, listener);
    };

    offMessage = (type, listener) => {
      this.#notifier.removeIframeListener(type, listener);
    };
  }

  return new IframeService();
});
